'use client'

import { AlertTriangle } from 'lucide-react'
import { Section, SectionHeading } from '@/components/platform/v2/Section'
import { ProductHero, Statement, FlowStrip, NextSteps, ProductCta, RelatedCases } from '@/components/platform/v2/ProductKit'
import { AutomationMock } from '@/components/platform/v2/Mocks'
import { AI_BALANCE, AI_LEVELS } from '@/data/pricing'

const EVENTS = [
    'Новая заявка',
    'Оплата прошла',
    'Сделка сменила этап',
    'Клиент зарегистрировался',
    'Покупка совершена',
    'Квиз завершён',
    'Событие клиента (визит, запись)',
]

const AI: { title: string; text: string }[] = [
    {
        title: 'AI-помощь с контентом · с BASE',
        text: 'Помогает писать тексты, описания товаров и услуг, заполнять карточки и готовить изображения.',
    },
    {
        title: 'AI-чат в приложении',
        text: 'Клиентский помощник доступен с BASE и работает по токенной модели внутри клиентского приложения.',
    },
    {
        title: 'AI-чат на сайте',
        text: 'Помощник для посетителей полноценного сайта: PRO / Сайт, PRO+ и GENIUS. Дополнительные расширения оплачиваются отдельно.',
    },
    {
        title: 'База знаний для AI',
        text: 'Материалы компании, на которые опирается помощник в ответах.',
    },
    {
        title: 'Дополнительные AI-баланс',
        text: 'Во всех платных тарифах есть AI-баланс. Дополнительное потребление оплачивается по токенной модели без смены тарифа.',
    },
    {
        title: 'Визуальный контент',
        text: 'AI-помощь для подготовки изображений страниц и карточек каталога доступна во всех платных тарифах.',
    },
    {
        title: 'AI в квизах и подборах',
        text: 'Расширенные сценарии выбора товаров и услуг в выбранном канале PRO или в обоих каналах PRO+ и GENIUS.',
    },
    {
        title: 'GENIUS · анализ бизнеса',
        text: 'Работа с данными CRM и процессов: сделки без движения, задачи без ответственных, отклонения и управленческие рекомендации.',
    },
    {
        title: 'GENIUS · AI для команды',
        text: 'Помощники владельца и сотрудников, управленческие выводы и AI-автоматизация доступных процессов.',
    },
]

const AutomationAiPage = () => {
    return (
        <div className="vcentre-public pt-24 sm:pt-28 md:pt-32">
            <ProductHero
                eyebrow="Автоматизация и AI"
                titleLines={['Система делает рутину.', 'Команда работает с клиентом.']}
                lead="Связывайте события, действия, CRM и коммуникации в автоматические сценарии, а AI используйте там, где он действительно помогает."
                primary={{ label: 'Найти, что можно автоматизировать', to: '/signin?focus=automation' }}
                secondary={{ label: 'CRM и клиенты', to: '/platform/crm' }}
                visual={
                    <img
                        src={'/assets/images/platform/automation-mascot-hero.png'}
                        alt="Маскот VCENTRE.IT со схемой автоматизации и фразой «Автоматизация рулит»"
                        width={1254}
                        height={1254}
                        loading="eager"
                        className="w-full max-w-[330px] animate-float drop-shadow-[0_30px_60px_rgba(0,0,0,0.6)] sm:max-w-[420px]"
                    />
                }
            />

            {/* АВТОМАТИЗАЦИЯ */}
            <Section tone="quiet">
                <SectionHeading
                    align="left"
                    eyebrow="Автоматизация"
                    title="Один понятный сценарий вместо шести ручных действий"
                    text="Каждое действие в цепочке команда делала бы руками — и иногда забывала бы."
                />
                <div className="mt-8">
                    <FlowStrip
                        steps={[
                            { title: 'Клиент оставил заявку' },
                            { title: 'Создать сделку' },
                            { title: 'Назначить менеджера' },
                            { title: 'Создать задачу' },
                            { title: 'Отправить сообщение' },
                            { title: 'Через 24 часа проверить статус', accent: true },
                        ]}
                        compact
                    />
                </div>
            </Section>

            {/* СЦЕНАРИИ */}
            <Section>
                <div className="grid gap-10 lg:grid-cols-[1fr_1fr] lg:items-start">
                    <div className="flex flex-col gap-5">
                        <p className="eyebrow">Конструктор сценариев</p>
                        <h2 className="display-2">
                            ЕСЛИ произошло событие —
                            <br />
                            <span className="text-primary">ТО выполнить действие.</span>
                        </h2>
                        <p className="lede">Сценарий собирается из событий платформы, без разработки под каждый случай.</p>
                        <div className="flex flex-wrap gap-2">
                            {EVENTS.map((e) => (
                                <span
                                    key={e}
                                    className="rounded-full border border-border/60 bg-[hsl(var(--charcoal))] px-3.5 py-2 text-sm text-foreground/85"
                                >
                                    {e}
                                </span>
                            ))}
                        </div>
                    </div>
                    <AutomationMock />
                </div>
            </Section>

            {/* AI */}
            <Section tone="quiet">
                <SectionHeading
                    eyebrow="AI в платформе"
                    title="AI — часть системы, а не отдельный продукт"
                    text="AI-помощь с контентом и клиентский помощник доступны с BASE. GENIUS добавляет AI для анализа и управления бизнесом."
                />
                <div className="mt-8 grid gap-4 md:grid-cols-3">
                    {AI_LEVELS.map((level) => (
                        <div key={level.title} className="surface-quiet">
                            <h3 className="font-bold text-primary">{level.title}</h3>
                            <p className="body-text mt-2">{level.text}</p>
                        </div>
                    ))}
                </div>
                <p className="body-text mt-6 text-center">
                    AI-баланс в месяц: {AI_BALANCE.map((balance) => `${balance.plan} — ${balance.value}`).join(' · ')}.
                    {' '}После его использования баланс можно пополнить.
                </p>
                <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {AI.map((a) => (
                        <div key={a.title} className="surface flex flex-col gap-2 text-center">
                            <h3 className="text-lg font-bold text-foreground sm:text-xl">{a.title}</h3>
                            <p className="body-text">{a.text}</p>
                        </div>
                    ))}
                </div>
            </Section>

            {/* AI + ДАННЫЕ */}
            <Section>
                <div className="grid gap-10 lg:grid-cols-[1fr_1fr] lg:items-center">
                    <div className="flex flex-col gap-4">
                        <p className="eyebrow">Контекст</p>
                        <h2 className="display-2">AI полезнее, когда понимает контекст</h2>
                        <p className="lede">
                            Помощник опирается на материалы компании и данные проекта, к которым ему открыт доступ. Мы не открываем AI всё
                            подряд — объём данных определяется настройками проекта.
                        </p>
                    </div>
                    <FlowStrip
                        steps={[
                            { title: 'База знаний', note: 'материалы компании' },
                            { title: 'Данные проекта', note: 'в разрешённом объёме' },
                            { title: 'Контекст клиента', note: 'текущий диалог' },
                            { title: 'AI-помощник', accent: true },
                        ]}
                    />
                </div>
            </Section>

            {/* БЕЗОПАСНОСТЬ */}
            <Section tone="quiet">
                <div className="grid gap-6 lg:grid-cols-[1fr_1fr] lg:items-center">
                    <Statement>
                        AI — помощник,
                        <br />
                        <span className="text-primary">а не автономный владелец бизнеса.</span>
                    </Statement>
                    <div className="surface-quiet flex flex-col gap-3">
                        <div className="flex items-start gap-3">
                            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                            <p className="text-sm font-semibold text-foreground">
                                «Вы общаетесь с ИИ, и он может допускать ошибки» — предупреждение показывается клиенту там, где с ним
                                общается AI.
                            </p>
                        </div>
                        <p className="body-text">
                            Ответственные действия остаются за человеком: подтверждение сделки, документы, финансовые решения. Настройки
                            определяют, что AI может делать сам, а что только предлагает команде.
                        </p>
                    </div>
                </div>
            </Section>

            <Section>
                <RelatedCases
                    slugs={['groverman', 'piter']}
                    text="Проекты, где часть повторяющейся работы делает система, а не менеджер."
                />
            </Section>

            <Section tone="quiet">
                <NextSteps
                    items={[
                        { to: '/platform/integrations', text: 'Подключите данные из внешних систем' },
                        { to: '/platform/communications', text: 'Свяжите сценарии с сообщениями клиенту' },
                    ]}
                />
            </Section>

            <Section>
                <ProductCta
                    title="Покажите, как сейчас работает ваш бизнес"
                    text="Мы покажем, что можно передать системе, а где оставить человека."
                    buttonLabel="Разобрать мои процессы"
                    to="/signin?focus=automation&source=automation-bottom"
                    secondary={{ to: '/platform', label: 'Обзор платформы' }}
                    visual={
                        <img
                            src={'/assets/images/platform/automation-mascot-cta.png'}
                            alt="Маскот VCENTRE.IT с ракетой и фразой «Запускаем быстро»"
                            width={1254}
                            height={1254}
                            loading="lazy"
                            className="w-full max-w-[260px] drop-shadow-[0_24px_48px_rgba(0,0,0,0.55)]"
                        />
                    }
                />
            </Section>
        </div>
    )
}

export default AutomationAiPage
